import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'design-system-web';
import { Flex, TabStrip } from 'components';
import { H3 } from 'components/typography';
import { FormProvider } from 'react-hook-form';
import type { Channel } from 'lane-shared/types/ChannelType';
import { MessageNotification } from './tabs/MessageNotification';
import { WorkOrderSurveys } from './tabs/WorkOrderSurveys';

import {
  useWorkOrderConfigurationTabs,
  Tabs,
} from './hooks/useWorkOrderConfigurationTabs';
import { useWorkOrderConfigurationForm } from './hooks/useWorkOrderConfigurationForm';

interface Props {
  channel?: Pick<Channel, '_id' | 'channelModules'>;
  refetchChannel?: () => void;
}

export const Configuration: React.FC<Props> = ({ channel, refetchChannel }) => {
  const { t } = useTranslation();
  const { tabs, selectedTab, setSelectedTab } = useWorkOrderConfigurationTabs();
  const { form, isDirty, isValid, saveConfiguration, undoConfiguration } =
    useWorkOrderConfigurationForm(channel, refetchChannel);

  const getTabComponent = (tabValue: string): React.ReactNode => {
    switch (tabValue) {
      case Tabs.MessageNotification:
        return <MessageNotification />;
      case Tabs.WorkOrderSurveys:
        return <WorkOrderSurveys />;
      default:
        return null;
    }
  };

  return (
    <div className="w-full mt-2">
      <div className="px-6">
        <Flex align="center" justify="space-between" className="py-2">
          <H3>{t('web.admin.work-order.configuration.heading')}</H3>
          <Flex align="center" gap={3}>
            <Button
              variant="secondary"
              size="medium"
              disabled={!isDirty}
              onClick={undoConfiguration}
            >
              {t('web.admin.work-order.configuration.undo')}
            </Button>
            <Button
              variant="primary"
              size="medium"
              disabled={!isValid || !isDirty}
              onClick={saveConfiguration}
              className="text-white"
            >
              {t('web.admin.work-order.configuration.save')}
            </Button>
          </Flex>
        </Flex>
      </div>

      {selectedTab && (
        <div className="px-6">
          <TabStrip
            tabs={tabs}
            selected={{ value: selectedTab.value }}
            onSelectTab={setSelectedTab}
            skipLabelTranslation
            className="justify-start"
          />

          <FormProvider {...form}>
            {getTabComponent(selectedTab.value)}
          </FormProvider>
        </div>
      )}
    </div>
  );
};
